import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";

const SB_URL   = import.meta.env.VITE_SUPABASE_URL  as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

interface FeatureFlag {
  key:         string;
  enabled:     boolean;
  description: string | null;
  updated_at:  string;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
    " · " +
    d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
  );
}

// ── Toggle ──────────────────────────────────────────────────────────────────
// Track: h-8 (32px) × w-14 (56px).  Thumb: h-6 w-6 (24px).
// Off → translate-x-1 (4px from left).  On → translate-x-7 (28px from left).
// Right gap in each state: 56 - 24 - 28 = 4px (on) / 56 - 24 - 4 = 28px (off).
interface ToggleProps {
  enabled:  boolean;
  isSaving: boolean;
  flagKey:  string;
  onToggle: (key: string, next: boolean) => void;
}

function Toggle({ enabled, isSaving, flagKey, onToggle }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={enabled ? `Disable ${flagKey}` : `Enable ${flagKey}`}
      disabled={isSaving}
      onClick={() => onToggle(flagKey, !enabled)}
      className={[
        "relative inline-flex h-8 w-14 shrink-0 items-center rounded-full",
        "transition-colors duration-200",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500",
        "focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950",
        enabled  ? "bg-orange-500" : "bg-gray-600",
        isSaving ? "opacity-50 pointer-events-none" : "cursor-pointer",
      ].join(" ")}
    >
      <span
        className={[
          "inline-block h-6 w-6 rounded-full bg-white shadow-md",
          "transition-transform duration-200",
          enabled ? "translate-x-7" : "translate-x-1",
        ].join(" ")}
      />
    </button>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export default function AdminFeatureFlags() {
  const { session } = useAuth();
  const jwt = session?.access_token;

  const [flags, setFlags]     = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [saving, setSaving]   = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    if (!jwt) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${SB_URL}/rest/v1/feature_flags?select=key,enabled,description,updated_at&order=key.asc`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } },
      );
      if (!res.ok) throw new Error(`${res.status}`);
      setFlags(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load feature flags");
    } finally {
      setLoading(false);
    }
  }, [jwt]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = useCallback(async (key: string, nextEnabled: boolean) => {
    if (!jwt || saving.has(key)) return;

    setFlags((prev) => prev.map((f) => f.key === key ? { ...f, enabled: nextEnabled } : f));
    setSaving((prev) => new Set(prev).add(key));

    try {
      const res = await fetch(
        `${SB_URL}/rest/v1/feature_flags?key=eq.${encodeURIComponent(key)}`,
        {
          method:  "PATCH",
          headers: {
            apikey:         ANON_KEY,
            Authorization:  `Bearer ${jwt}`,
            "Content-Type": "application/json",
            Prefer:         "return=representation",
          },
          body: JSON.stringify({ enabled: nextEnabled, updated_at: new Date().toISOString() }),
        },
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const [updated] = await res.json() as [FeatureFlag];
      setFlags((prev) => prev.map((f) => f.key === key ? { ...f, ...updated } : f));
    } catch (e) {
      setFlags((prev) => prev.map((f) => f.key === key ? { ...f, enabled: !nextEnabled } : f));
      setError(e instanceof Error ? e.message : "Failed to update flag");
    } finally {
      setSaving((prev) => { const s = new Set(prev); s.delete(key); return s; });
    }
  }, [jwt, saving]);

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-semibold">Feature Flags</h1>

      {loading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-16 rounded-xl border border-gray-800 bg-gray-900/40 animate-pulse" />
          ))}
        </div>
      )}

      {error && <p className="text-sm text-red-400">{error}</p>}

      {!loading && !error && flags.length === 0 && (
        <p className="text-sm text-gray-500">No feature flags configured.</p>
      )}

      {!loading && flags.length > 0 && (
        <div className="rounded-xl border border-gray-800 overflow-hidden divide-y divide-gray-800">
          {flags.map((flag) => (
            <div key={flag.key} className="flex items-center justify-between gap-6 px-5 py-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-100 font-mono">{flag.key}</p>
                {flag.description && (
                  <p className="mt-0.5 text-xs text-gray-500">{flag.description}</p>
                )}
                <p className="mt-1 text-xs text-gray-700">Updated {fmtDate(flag.updated_at)}</p>
              </div>

              <div className="flex items-center gap-4 shrink-0">
                <span className={`text-xs font-medium w-5 text-right ${flag.enabled ? "text-orange-400" : "text-gray-500"}`}>
                  {flag.enabled ? "On" : "Off"}
                </span>
                <Toggle
                  enabled={flag.enabled}
                  isSaving={saving.has(flag.key)}
                  flagKey={flag.key}
                  onToggle={handleToggle}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
